import React from "react";
import { Link, useLocation } from "react-router-dom";
import { getRankingsUrl, getRankingsSeasonLinkUrl, getCollegeUrl, getCollegeHubOrDetailPath, getPlayersUrl, StatCategory } from "../../utils/urlUtils";
import { getCollegeCareerPath } from "../../utils/collegeCareer";
import { getConferenceCareerPath } from "../../utils/conferenceCareer";
import { getPlayerCareerPath } from "../../utils/playerCareer";
import { CURRENT_YEAR } from "../../constants/app";
import { getConferenceNameFromAbbreviation } from "../../utils/conferenceUtils";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  preserveSearchParams?: boolean; // If true, preserve current URL search params when clicking breadcrumbs
}

/**
 * Breadcrumbs Component
 * 
 * Provides visual breadcrumb navigation for SEO and user experience
 * Renders as a horizontal list of links showing the page hierarchy
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, preserveSearchParams = false }) => {
  const location = useLocation();
  
  if (!items || items.length === 0) {
    return null;
  }

  // Get current search params if we should preserve them, but exclude conferenceView and conferenceAbbr
  let currentSearch = '';
  if (preserveSearchParams && location.search) {
    const searchParams = new URLSearchParams(location.search);
    // Remove conferenceView and conferenceAbbr parameters
    searchParams.delete('conferenceView');
    searchParams.delete('conferenceAbbr');
    // Rebuild search string if there are remaining params
    const filteredSearch = searchParams.toString();
    currentSearch = filteredSearch ? `?${filteredSearch}` : '';
  }

  return (
    <nav aria-label="Breadcrumb" style={{ marginBottom: 0 }}>
      <ol
        style={{
          display: "flex",
          flexWrap: "wrap",
          listStyle: "none",
          padding: 0,
          margin: 0,
          alignItems: "center",
          fontSize: "0.875rem",
        }}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const url = preserveSearchParams && currentSearch ? `${item.url}${currentSearch}` : item.url;
          
          return (
            <li
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              {isLast ? (
                <span
                  style={{
                    color: "rgba(255, 255, 255, 0.7)",
                    fontWeight: "500",
                  }}
                  aria-current="page"
                >
                  {item.name}
                </span>
              ) : (
                <>
                  <Link
                    to={url}
                    style={{
                      color: "#60a5fa",
                      textDecoration: "none",
                      cursor: "pointer",
                      zIndex: 1,
                      position: "relative",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = "underline";
                      e.currentTarget.style.color = "#93c5fd";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = "none";
                      e.currentTarget.style.color = "#60a5fa";
                    }}
                  >
                    {item.name}
                  </Link>
                  <span
                    style={{
                      margin: "0 8px",
                      color: "rgba(255, 255, 255, 0.5)",
                    }}
                    aria-hidden="true"
                  >
                    /
                  </span>
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

/**
 * Generates breadcrumb items for rankings pages
 */
export const getRankingsBreadcrumbs = (
  statCategory?: string,
  year?: number,
  week?: number,
  yearAggregate: boolean = true,
  conferenceView: boolean = false,
  conferenceAbbr?: string | null
): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [
    { name: "Home", url: "/" },
  ];

  // Map stat category to display name
  const STAT_FILTER_NAMES: Record<string, string> = {
    '': 'Fantasy Points',
    'passing': 'Passing',
    'rushing': 'Rushing',
    'receiving': 'Receiving',
    'defense': 'Defense',
    'kicking': 'Kicking',
    'punting': 'Punting',
    'returning': 'Returning',
    'scoring': 'Scoring',
  };

  // Combine stat category and year into a single breadcrumb when year is present
  // Note: Base rankings breadcrumb does NOT include conferenceView (links back to non-conference view)
  if (year !== undefined) {
    // Year is present - combine stat category and year
    if (statCategory) {
      const statName = STAT_FILTER_NAMES[statCategory] || statCategory;
      breadcrumbs.push({
        name: `${statName} Rankings - ${year} Season`,
        url: getRankingsUrl(statCategory as StatCategory, year, undefined, true, false),
      });
    } else {
      breadcrumbs.push({
        name: `Rankings - ${year} Season`,
        url: getRankingsUrl('' as StatCategory, year, undefined, true, false),
      });
    }

    // Add week breadcrumb if week is specified (and not year aggregate)
    if (!yearAggregate && week !== undefined) {
      breadcrumbs.push({
        name: `Week ${week}`,
        url: getRankingsUrl(statCategory as StatCategory, year, week, false, false),
      });
    }
  } else {
    // No year - just show stat category breadcrumb
    if (statCategory) {
      const statName = STAT_FILTER_NAMES[statCategory] || statCategory;
      breadcrumbs.push({
        name: `${statName} Rankings`,
        url: getRankingsUrl(statCategory as StatCategory, undefined, undefined, true, false),
      });
    } else {
      breadcrumbs.push({
        name: "Rankings",
        url: "/rankings",
      });
    }
  }

  // Add conference view breadcrumb if in conference view
  if (conferenceView) {
    breadcrumbs.push({
      name: "Conference Rankings",
      url: getRankingsUrl(statCategory as StatCategory, year, yearAggregate ? undefined : week, yearAggregate, true),
    });

    // Add conference team view breadcrumb if a specific conference is selected
    if (conferenceAbbr) {
      const conferenceName = getConferenceNameFromAbbreviation(conferenceAbbr) || conferenceAbbr.toUpperCase();
      breadcrumbs.push({
        name: conferenceName,
        url: getRankingsUrl(statCategory as StatCategory, year, yearAggregate ? undefined : week, yearAggregate, true, conferenceAbbr),
      });
    }
  }

  return breadcrumbs;
};

/**
 * Generates breadcrumb items for college detail pages
 * 
 * SEO-optimized breadcrumb hierarchy that matches URL structure:
 * Home → {StatCategory} Rankings → {Year} Season → Week {Week} → {College Name}
 * 
 * This structure follows the URL pattern: /college/{slug}/{statFilter}/{year}/week/{week}
 * and links back to corresponding rankings pages for better navigation and SEO.
 */
export const getCollegeBreadcrumbs = (
  collegeName: string,
  collegeSlug: string,
  statFilter: string,
  year?: number,
  week?: number,
  yearAggregate: boolean = true,
  conferenceView: boolean = false,
  conferenceAbbr?: string | null
): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [
    { name: "Home", url: "/" },
  ];

  // Map stat category to display name
  const STAT_FILTER_NAMES: Record<string, string> = {
    '': 'Fantasy Points',
    'passing': 'Passing',
    'rushing': 'Rushing',
    'receiving': 'Receiving',
    'defense': 'Defense',
    'kicking': 'Kicking',
    'punting': 'Punting',
    'returning': 'Returning',
    'scoring': 'Scoring',
  };

  // Combine stat category and year into a single breadcrumb when year is present
  // Note: Base rankings breadcrumb does NOT include conferenceView (links back to non-conference view)
  if (year === CURRENT_YEAR && yearAggregate && !conferenceView) {
    // Current season: link up to the yearless stat hub, which shows this season
    breadcrumbs.push({
      name: statFilter ? `${STAT_FILTER_NAMES[statFilter] || statFilter} Rankings` : "Rankings",
      url: getRankingsSeasonLinkUrl(statFilter as StatCategory, year),
    });
  } else if (year !== undefined) {
    // Year is present - combine stat category and year
    if (statFilter) {
      const statName = STAT_FILTER_NAMES[statFilter] || statFilter;
      breadcrumbs.push({
        name: `${statName} Rankings - ${year} Season`,
        url: getRankingsUrl(statFilter as StatCategory, year, undefined, true, false),
      });
    } else {
      breadcrumbs.push({
        name: `Rankings - ${year} Season`,
        url: getRankingsUrl('' as StatCategory, year, undefined, true, false),
      });
    }

    // Add week breadcrumb if week is specified (and not year aggregate)
    if (!yearAggregate && week !== undefined) {
      breadcrumbs.push({
        name: `Week ${week}`,
        url: getRankingsUrl(statFilter as StatCategory, year, week, false, false),
      });
    }
  } else {
    // No year - just show stat category breadcrumb
    if (statFilter) {
      const statName = STAT_FILTER_NAMES[statFilter] || statFilter;
      breadcrumbs.push({
        name: `${statName} Rankings`,
        url: getRankingsUrl(statFilter as StatCategory, undefined, undefined, true, false),
      });
    } else {
      breadcrumbs.push({
        name: "Rankings",
        url: getRankingsUrl('' as StatCategory, undefined, undefined, true, false),
      });
    }
  }

  // Add conference view breadcrumbs if coming from conference view
  if (conferenceView) {
    breadcrumbs.push({
      name: "Conference Rankings",
      url: getRankingsUrl(statFilter as StatCategory, year, yearAggregate ? undefined : week, yearAggregate, true),
    });

    // Add conference team view breadcrumb if a specific conference is selected
    if (conferenceAbbr) {
      const conferenceName = getConferenceNameFromAbbreviation(conferenceAbbr) || conferenceAbbr.toUpperCase();
      breadcrumbs.push({
        name: conferenceName,
        url: getRankingsUrl(statFilter as StatCategory, year, yearAggregate ? undefined : week, yearAggregate, true, conferenceAbbr),
      });
    }
  }

  const collegeCrumbPath =
    year === undefined && statFilter
      ? getCollegeUrl(collegeSlug, statFilter, undefined, week, yearAggregate)
      : getCollegeHubOrDetailPath(
          collegeSlug,
          statFilter || undefined,
          year ?? CURRENT_YEAR,
          week,
          yearAggregate,
          CURRENT_YEAR
        );

  breadcrumbs.push({
    name: collegeName,
    url: collegeCrumbPath,
  });

  return breadcrumbs;
};

/**
 * Generates breadcrumb items for About page
 */
export const getPlayersBreadcrumbs = (
  statCategory?: string,
  year?: number,
  week?: number,
  yearAggregate: boolean = true,
  view: "leaders" | "rookies" | "rookies-by-college" = "leaders"
): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [{ name: "Home", url: "/" }];

  if (view === "rookies" || view === "rookies-by-college") {
    breadcrumbs.push({ name: "Players", url: "/players" });
    if (year != null) {
      breadcrumbs.push({
        name: `${year} NFL Rookie Rankings`,
        url: `/players/rookies/${year}`,
      });
      if (view === "rookies-by-college") {
        breadcrumbs.push({
          name: "By College",
          url: `/players/rookies/${year}/by-college`,
        });
      }
    }
    return breadcrumbs;
  }

  const STAT_FILTER_NAMES: Record<string, string> = {
    '': 'Fantasy Points',
    'passing': 'Passing',
    'rushing': 'Rushing',
    'receiving': 'Receiving',
    'defense': 'Defense',
    'kicking': 'Kicking',
    'punting': 'Punting',
    'returning': 'Returning',
    'scoring': 'Scoring',
  };

  if (year !== undefined) {
    if (statCategory) {
      const statName = STAT_FILTER_NAMES[statCategory] || statCategory;
      breadcrumbs.push({
        name: `${statName} Players - ${year}`,
        url: getPlayersUrl(statCategory as StatCategory, year, undefined, true),
      });
    } else {
      breadcrumbs.push({
        name: `Players - ${year}`,
        url: getPlayersUrl('' as StatCategory, year, undefined, true),
      });
    }
    if (!yearAggregate && week !== undefined) {
      breadcrumbs.push({
        name: `Week ${week}`,
        url: getPlayersUrl(statCategory as StatCategory, year, week, false),
      });
    }
  } else if (statCategory) {
    const statName = STAT_FILTER_NAMES[statCategory] || statCategory;
    breadcrumbs.push({
      name: `${statName} Players`,
      url: getPlayersUrl(statCategory as StatCategory),
    });
  } else {
    breadcrumbs.push({ name: "Players", url: "/players" });
  }

  return breadcrumbs;
};

export const getAboutBreadcrumbs = (): BreadcrumbItem[] => {
  return [
    { name: "Home", url: "/" },
    { name: "About", url: "/about" },
  ];
};

export const getPlayersCareerBreadcrumbs = (stat: string = ""): BreadcrumbItem[] => {
  const STAT_FILTER_NAMES: Record<string, string> = {
    passing: "Passing",
    rushing: "Rushing",
    receiving: "Receiving",
    defense: "Defense",
    kicking: "Kicking",
    punting: "Punting",
    returning: "Returning",
  };
  const items: BreadcrumbItem[] = [
    { name: "Home", url: "/" },
    { name: "Players", url: "/players" },
    { name: "Career", url: getPlayerCareerPath() },
  ];
  if (stat) {
    items.push({
      name: STAT_FILTER_NAMES[stat] || stat,
      url: getPlayerCareerPath(stat),
    });
  }
  return items;
};

export const getCollegeCareerBreadcrumbs = (
  collegeName: string,
  collegeSlug: string,
  stat: string = ""
): BreadcrumbItem[] => {
  const STAT_FILTER_NAMES: Record<string, string> = {
    passing: "Passing",
    rushing: "Rushing",
    receiving: "Receiving",
    defense: "Defense",
    kicking: "Kicking",
    punting: "Punting",
    returning: "Returning",
  };
  const items: BreadcrumbItem[] = [
    { name: "Home", url: "/" },
    { name: collegeName, url: `/college/${collegeSlug}` },
    { name: "Career", url: getCollegeCareerPath(collegeSlug) },
  ];
  if (stat) {
    items.push({
      name: STAT_FILTER_NAMES[stat] || stat,
      url: getCollegeCareerPath(collegeSlug, stat),
    });
  }
  return items;
};

export const getConferenceCareerBreadcrumbs = (
  conferenceName: string | null,
  abbr: string | null,
  stat: string = ""
): BreadcrumbItem[] => {
  const STAT_FILTER_NAMES: Record<string, string> = {
    passing: "Passing",
    rushing: "Rushing",
    receiving: "Receiving",
    defense: "Defense",
    kicking: "Kicking",
    punting: "Punting",
    returning: "Returning",
  };
  const items: BreadcrumbItem[] = [
    { name: "Home", url: "/" },
    { name: "Conferences", url: getConferenceCareerPath() },
  ];
  if (abbr) {
    items.push({
      name: conferenceName || abbr.toUpperCase(),
      url: getConferenceCareerPath(abbr),
    });
  }
  if (stat) {
    items.push({
      name: STAT_FILTER_NAMES[stat] || stat,
      url: getConferenceCareerPath(abbr, stat),
    });
  }
  return items;
};

export const getClassesBreadcrumbs = (
  classYear?: number | null,
  view?: "college" | "conference" | "players",
  collegeName?: string | null,
  collegeSlug?: string | null
): BreadcrumbItem[] => {
  const items: BreadcrumbItem[] = [
    { name: "Home", url: "/" },
    { name: "Classes", url: "/classes" },
  ];
  if (classYear == null) return items;
  items.push({ name: `Class of ${classYear}`, url: `/classes/${classYear}` });
  if (view === "conference") {
    items.push({ name: "Conferences", url: `/classes/${classYear}/conference` });
  }
  if (view === "players" && collegeName) {
    items.push({
      name: collegeName,
      url: collegeSlug ? `/classes/${classYear}/${collegeSlug}` : `/classes/${classYear}`,
    });
  }
  return items;
};

